import * as logging from 'utils/Logging'
import * as messagesArea from 'utils/Messages'
import * as variable from 'utils/Variable'

import { borrowerApiClient } from 'api/BorrowerApiClient'
import { Loan } from 'api/BorrowerApiSchemas'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface FetchLoanDataProps {
  loans: Loan[]
  loanError: string
  sideMenu: { loanCount: number; missedArrears: string; missedReaged: string }
  isLoading: boolean
  fetchLoanData: () => void
}

const useLoansApiRequest = (): FetchLoanDataProps => {
  const borrowerApi = borrowerApiClient()
  const [sideMenu, setSideMenu] = useState({ loanCount: 0, missedArrears: '', missedReaged: '' })

  const [loans, setLoans] = useState<Loan[]>([])
  const [isLoading, setLoading] = useState<boolean>(true)

  const [loanError, setLoanError] = useState<string>('')

  const sessReference = window.sessionStorage.getItem(variable.defaultRef)

  const navigate = useNavigate()

  const fetchLoanData = () => {
    borrowerApi
      .getLoans()
      .then(
        (loans) => {
          if (loans.length > 0) {
            let arrears = 0
            let reaged = 0

            for (let i = 0; i < loans.length; i++) {
              arrears += Number(loans[i].plan.missedArrearsTotal)
              reaged += Number(loans[i].plan.missedReAgedTotal)
            }

            setSideMenu({
              loanCount: loans.length,
              missedArrears: arrears !== 0 ? 'yes' : 'no',
              missedReaged: reaged !== 0 ? 'yes' : 'no',
            })

            setLoans(loans)

            // add the first reference into cookie
            const firstRecord = loans[0]
            const reference = firstRecord.plan.reference
            window.sessionStorage.setItem(variable.defaultRef, reference)
            window.sessionStorage.setItem(variable.loanAmount, loans[0].plan.amount.toString())

            // Send the customer to the dashboard when the loan they had selected is no longer one of
            // theirs — it has been settled, or they are on a different account — because every screen
            // below reads that reference and would otherwise render another customer's loan or none.
            //
            // Only when there *was* a selection. `sessReference` is read when the hook renders, before
            // this fetch, so on a fresh session it is null while the line above has just written the real
            // reference. Treating that as a mismatch redirected the first authenticated page load of
            // every session to the dashboard, which silently discarded any deep link (TED-4200 stores one
            // and navigates to it, and this landed on top of it a moment later).
            const selectionIsStale = sessReference !== null && loans.every((it) => it.plan.reference !== sessReference)
            if (selectionIsStale) {
              navigate('/borrowing-centre')
            }
          }
        },
        (error) => {
          logging.logError('fetchLoanData', error)
          if (
            error.code === variable.httpStatusCodes.notFound ||
            error.code === variable.httpStatusCodes.unauthorized
          ) {
            setLoanError(messagesArea.errorMessagesLoan)
          } else {
            if (error && error.message) {
              setLoanError(error.message)
            }
          }
        },
      )
      .finally(() => {
        setLoading(false)
      })
  }

  return {
    loans,
    loanError,
    sideMenu,
    isLoading,
    fetchLoanData,
  }
}

export default useLoansApiRequest
